import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  FirestoreDataConverter,
} from 'firebase/firestore';
import type { Actor } from '@segmently/schema-models/entities';
import { CoreFirebaseService } from './CoreFirebaseService';
import { actorConverter as clientActorConverter } from '../converters/actorConverter';

/**
 * ActorService
 *
 * Type-safe service for Actor entity CRUD operations.
 * Actors are stored as subcollection under audiences.
 *
 * Path: projects/{projectId}/audiences/{audienceId}/actors/{actorId}
 *
 * Features:
 * - Basic CRUD operations (create, read, delete, list)
 * - Type-safe: only Actor types exposed, no Firebase types in public API
 * - Scoped to parent Audience
 * - Uses actorConverter for Date↔Timestamp conversion
 *
 * Usage:
 * ```typescript
 * import { ActorService } from '@/modules/firebase-core/services/ActorService';
 *
 * const service = new ActorService('project-id', 'audience-id');
 *
 * // Create
 * const actor = await service.create({
 *   name: 'Actor Name',
 *   description: 'Description',
 *   weight: 1
 * });
 *
 * // Read
 * const found = await service.get(actor.id!);
 *
 * // List all actors
 * const actors = await service.list({ limit: 100 });
 *
 * // Delete
 * await service.delete(actor.id!);
 * ```
 */
export class ActorService {
  private readonly collectionName = 'actors';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<Actor>;
  private readonly projectId: string;
  private readonly audienceId: string;

  /**
   * Create a new ActorService instance
   *
   * @param projectId - Parent project ID
   * @param audienceId - Parent audience ID
   * @param converter - Firestore converter for Actor entity (defaults to client SDK converter)
   */
  constructor(
    projectId: string,
    audienceId: string,
    converter: FirestoreDataConverter<Actor> = clientActorConverter
  ) {
    this.projectId = projectId;
    this.audienceId = audienceId;
    this.converter = converter;
  }

  /**
   * Get the subcollection path for actors
   */
  private getCollectionPath(): string {
    return `projects/${this.projectId}/audiences/${this.audienceId}/${this.collectionName}`;
  }

  /**
   * Create a new actor
   *
   * @param data - Partial actor data (id will be auto-generated if not provided)
   * @returns {Promise<Actor>} The created actor with generated ID
   */
  async create(data: Partial<Actor>): Promise<Actor> {
    try {
      const collectionRef = collection(this.firestore, this.getCollectionPath());
      const docRef = doc(collectionRef).withConverter(this.converter);

      const actorData: Actor = {
        ...data,
        id: docRef.id,
        creationDate: new Date(),
      } as Actor;

      await setDoc(docRef, actorData);

      return actorData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ActorService.create');
      throw error;
    }
  }

  /**
   * Get an actor by ID
   *
   * @param id - Actor ID
   * @returns {Promise<Actor | null>} The actor or null if not found
   */
  async get(id: string): Promise<Actor | null> {
    try {
      const docRef = doc(this.firestore, this.getCollectionPath(), id).withConverter(this.converter);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return { ...data, id: docSnap.id } as Actor;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ActorService.get');
      throw error;
    }
  }

  /**
   * List actors for the audience
   *
   * @param options - Query options
   * @param options.limit - Maximum number of actors to return (default: 100)
   * @returns {Promise<Actor[]>} Array of actors
   */
  async list(options: { limit?: number } = {}): Promise<Actor[]> {
    try {
      const collectionRef = collection(this.firestore, this.getCollectionPath()).withConverter(this.converter);

      const pageLimit = options.limit || 100;
      const q = query(collectionRef, orderBy('creationDate', 'desc'), limit(pageLimit));

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Actor));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ActorService.list');
      throw error;
    }
  }

  /**
   * Update scalar actor fields (Spec C C2 — the managed weight write path).
   * Uses updateDoc so untouched keys (incl. JTBD subcollections) stay intact.
   *
   * @param id - Actor ID
   * @param data - Partial scalar fields (name, description, weight, role, influence)
   */
  async update(id: string, data: Partial<Pick<Actor, 'name' | 'description' | 'weight' | 'role' | 'influence'>>): Promise<void> {
    try {
      const docRef = doc(this.firestore, this.getCollectionPath(), id);
      const payload: Record<string, unknown> = { lastModified: new Date() };
      for (const [key, value] of Object.entries(data)) {
        if (value !== undefined) payload[key] = value;
      }
      await updateDoc(docRef, payload);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ActorService.update');
      throw error;
    }
  }

  /**
   * Delete an actor
   *
   * @param id - Actor ID
   * @returns {Promise<void>}
   */
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(this.firestore, this.getCollectionPath(), id);
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ActorService.delete');
      throw error;
    }
  }

  /**
   * Load actor with all subcollections
   *
   * @param actorId - Actor ID
   * @returns {Promise<Actor>} Actor with all subcollections loaded
   */
  async getWithSubcollections(actorId: string): Promise<Actor | null> {
    try {
      // Load base actor data
      const actor = await this.get(actorId);
      if (!actor) return null;

      // Load all subcollections in parallel
      const [coreJobs, contexts, fears, bigJobs, subtasks, competitors, habits] = await Promise.all([
        this.loadSubcollection(actorId, 'coreJobs'),
        this.loadSubcollection(actorId, 'contexts'),
        this.loadSubcollection(actorId, 'fears'),
        this.loadSubcollection(actorId, 'bigJobs'),
        this.loadSubcollection(actorId, 'subtasks'),
        this.loadSubcollection(actorId, 'competitors'),
        this.loadSubcollection(actorId, 'habits'),
      ]);

      // Attach subcollections to actor
      actor.coreJobs = coreJobs;
      actor.contexts = contexts;
      actor.fears = fears;
      actor.bigJobs = bigJobs;
      actor.subtasks = subtasks;
      actor.competitors = competitors;
      actor.habits = habits;

      return actor;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ActorService.getWithSubcollections');
      throw error;
    }
  }

  /**
   * Load a subcollection for an actor
   *
   * @param actorId - Actor ID
   * @param subcollectionName - Name of subcollection (coreJobs, fears, etc.)
   * @returns {Promise<any[]>} Array of subcollection items
   */
  private async loadSubcollection(actorId: string, subcollectionName: string): Promise<any[]> {
    try {
      const subcollectionPath = `${this.getCollectionPath()}/${actorId}/${subcollectionName}`;
      const collectionRef = collection(this.firestore, subcollectionPath);
      const querySnapshot = await getDocs(collectionRef);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));
    } catch (error) {
      // Если субколлекция не существует, возвращаем пустой массив
      console.warn(`Subcollection ${subcollectionName} not found for actor ${actorId}`);
      return [];
    }
  }
}
